import { useCallback, useEffect, useRef, useState } from "react";

// Keep a saved spot on today's screen (a spot saved on a bigger window would hide the tile off the edge).
const onScreen = (p) => ({ x: Math.max(0, Math.min(window.innerWidth - 124, p.x)), y: Math.max(0, Math.min(window.innerHeight - 94, p.y)) });

// Drag a fixed-position element anywhere; position is remembered in localStorage under `key`.
export const useDragPosition = (key, defaultPos, enabled = true) => {
  const [pos, setPos] = useState(() => { try { const p = JSON.parse(localStorage.getItem(key)); return Number.isFinite(p?.x) && Number.isFinite(p?.y) ? onScreen(p) : defaultPos(); } catch { return defaultPos(); } });
  useEffect(() => { const f = () => setPos((p) => onScreen(p)); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  const start = useRef(null);
  const onPointerDown = useCallback((e) => {
    if (!enabled) return;
    const btn = e.target.closest("button");
    if (btn && btn !== e.currentTarget) return;
    const r = e.currentTarget.getBoundingClientRect();
    start.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y, w: Math.max(80, r.width), h: Math.max(80, r.height), moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [enabled, pos]);
  const onPointerMove = useCallback((e) => {
    if (!start.current) return;
    const { w, h } = start.current;
    const dx = e.clientX - start.current.x, dy = e.clientY - start.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) start.current.moved = true;
    setPos({ x: Math.max(0, Math.min(window.innerWidth - w, start.current.px + dx)), y: Math.max(0, Math.min(window.innerHeight - h, start.current.py + dy)) });
  }, []);
  const onPointerUp = useCallback(() => {
    if (start.current) localStorage.setItem(key, JSON.stringify(pos));
    const moved = start.current?.moved; start.current = null; return moved;
  }, [key, pos]);
  const save = useCallback((p) => { setPos(p); localStorage.setItem(key, JSON.stringify(p)); }, [key]);
  return { pos, setPos, save, handlers: { onPointerDown, onPointerMove, onPointerUp }, wasDrag: () => !!start.current?.moved };
};

// A round minimized tile you can drag anywhere (tap = expand). Shared by the music and radio players.
export const DraggableTile = ({ posKey, defaultPos, onTap, className = "", children, testId, ariaLabel }) => {
  const drag = useDragPosition(posKey, defaultPos);
  const moved = useRef(false);
  return (
    <button
      type="button"
      style={{ left: drag.pos.x, top: drag.pos.y }}
      className={`fixed z-[70] touch-none select-none cursor-grab active:cursor-grabbing ${className}`}
      onPointerDown={(e) => { moved.current = false; drag.handlers.onPointerDown(e); }}
      onPointerMove={(e) => { drag.handlers.onPointerMove(e); moved.current = drag.wasDrag(); }}
      onPointerUp={(e) => { drag.handlers.onPointerUp(e); if (!moved.current) onTap(); }}
      aria-label={ariaLabel}
      title="Drag me anywhere · tap to open"
      data-testid={testId}
    >
      {children}
    </button>
  );
};

export const tileDefault = (side = "right") => () => {
  const mobile = window.innerWidth < 640;
  const y = Math.max(8, window.innerHeight - 72 - (mobile ? 88 : 16));
  return side === "left" ? { x: 16, y } : { x: Math.max(8, window.innerWidth - 72 - 16), y };
};
